import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Tag,
  Popconfirm,
  InputNumber,
  Drawer,
  Card,
  Typography,
  Descriptions,
  Alert,
  Row,
  Col,
  Statistic,
  Tooltip,
  Badge,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  ReloadOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { registrarService } from '../../services/registrarService';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

// Capability label map — keep in sync with api/app/plugin/registrar/capabilities/*.php
const CAPABILITY_LABELS = {
  SupportsBulkAvailability: '批量可用性',
  SupportsBulkDomainList: '批量域名列表',
  SupportsChildNameservers: '子域名服务器',
  SupportsContactHandles: '联系人句柄',
  SupportsDnssec: 'DNSSEC',
  SupportsRestore: '赎回期恢复',
};

export default function Registrars() {
  const [loading, setLoading] = useState(false);
  const [registrars, setRegistrars] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegistrar, setEditingRegistrar] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRegistrar, setSelectedRegistrar] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingRowId, setTestingRowId] = useState(null);
  const [syncingPricing, setSyncingPricing] = useState(false);
  const [syncingRowId, setSyncingRowId] = useState(null);
  const [form] = Form.useForm();

  // Sync queue health
  const [syncHealth, setSyncHealth] = useState(null);
  const [syncStats, setSyncStats] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Sync result modal (after pricing sync)
  const [syncResultModal, setSyncResultModal] = useState({ visible: false, result: null });

  useEffect(() => {
    fetchRegistrars();
    fetchPlugins();
    fetchHealth();
  }, []);

  // 插件 -> 能力索引（plugin_name -> capabilities[]）
  const capabilitiesByPlugin = useMemo(() => {
    const map = {};
    plugins.forEach((p) => {
      const caps = p.capabilities || p.supports || [];
      map[p.name] = Array.isArray(caps) ? caps : [];
    });
    return map;
  }, [plugins]);

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const [q, s] = await Promise.all([
        registrarService.getSyncQueue().catch(() => null),
        registrarService.getSyncStatistics('today').catch(() => null),
      ]);
      if (q?.code === 0 || q?.success) setSyncHealth(q.data);
      if (s?.code === 0 || s?.success) setSyncStats(s.data);
    } finally {
      setHealthLoading(false);
    }
  };

  const fetchRegistrars = async () => {
    setLoading(true);
    try {
      const response = await registrarService.getRegistrars();
      if (response.success) {
        setRegistrars(response.data);
      }
    } catch (error) {
      message.error('获取注册商列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlugins = async () => {
    try {
      const response = await registrarService.getPlugins();
      if (response.success) {
        setPlugins(response.data);
      }
    } catch (error) {
      message.error('获取插件列表失败');
    }
  };

  const handleCreate = () => {
    setEditingRegistrar(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      priority: 100,
      sandbox_mode: false,
      test_connection: true,
      auto_sync_pricing: false,
      dnssec: true,
      idn: true,
    });
    setModalVisible(true);
  };

  const handleEdit = async (registrar) => {
    setEditingRegistrar(registrar);

    // 获取完整的注册商信息（包括API凭证）
    try {
      const response = await registrarService.getRegistrar(registrar.id);
      if (response.success) {
      const fullRegistrar = response.data;

      // 设置基本字段
      const formValues = {
        display_name: fullRegistrar.name,
        plugin_name: fullRegistrar.code,
        description: fullRegistrar.notes,
        currency: fullRegistrar.currency || 'USD',
        enabled: fullRegistrar.is_active,
        sandbox_mode: !!fullRegistrar.sandbox_mode,
        priority: fullRegistrar.priority,
        test_connection: false,
        dnssec: fullRegistrar.features?.dnssec ?? true,
        idn: fullRegistrar.features?.idn ?? true,
      };

        // 设置API凭证字段（直接使用原始凭证）
        if (fullRegistrar.api_credentials) {
          Object.keys(fullRegistrar.api_credentials).forEach((key) => {
            formValues[`credential_${key}`] = fullRegistrar.api_credentials[key];
          });
        }

        form.setFieldsValue(formValues);
        setModalVisible(true);
      }
    } catch (error) {
      message.error('获取注册商信息失败');
    }
  };

  const handleViewDetails = async (registrar) => {
    setSelectedRegistrar(null);
    setDrawerVisible(true);

    try {
      const response = await registrarService.getRegistrar(registrar.id);
      if (response.success) {
        setSelectedRegistrar(response.data);
      }
    } catch (error) {
      message.error('获取注册商详情失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 构建API凭证对象
      const selectedPlugin = plugins.find((p) => p.name === values.plugin_name);
      const credentials = {};

      if (selectedPlugin && selectedPlugin.credential_fields) {
        selectedPlugin.credential_fields.forEach((field) => {
          const value = values[`credential_${field.name}`];
          if (value) {
            credentials[field.name] = value;
          }
        });
      }

      const data = {
        display_name: values.display_name,
        plugin_name: values.plugin_name,
        credentials: credentials,
        description: values.description,
        currency: values.currency || 'USD',
        enabled: values.enabled,
        priority: values.priority,
        sandbox_mode: !!values.sandbox_mode,
        test_connection: values.test_connection,
        auto_sync_pricing: values.auto_sync_pricing,
        features: {
          dnssec: values.dnssec,
          glue: true,
          idn: values.idn,
        },
      };

      setLoading(true);

      if (editingRegistrar) {
        data.id = editingRegistrar.id;
        const response = await registrarService.updateRegistrar(data);

        if (response.success) {
          message.success('更新注册商成功');
          setModalVisible(false);
          fetchRegistrars();
        } else {
          message.error(response.error || '更新注册商失败');
        }
      } else {
        const response = await registrarService.createRegistrar(data);

        if (response.success) {
          message.success('创建注册商成功');
          setModalVisible(false);
          fetchRegistrars();
        } else {
          message.error(response.error || '创建注册商失败');
        }
      }
    } catch (error) {
      console.error('提交失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await registrarService.deleteRegistrar(id);

      if (response.success) {
        message.success('删除成功');
        fetchRegistrars();
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const values = await form.validateFields();
      const selectedPlugin = plugins.find((p) => p.name === values.plugin_name);
      const credentials = {};

      if (selectedPlugin && selectedPlugin.credential_fields) {
        selectedPlugin.credential_fields.forEach((field) => {
          if (values[`credential_${field.name}`]) {
            credentials[field.name] = values[`credential_${field.name}`];
          }
        });
      }

      setTestingConnection(true);

      const response = await registrarService.testConnection(
        values.plugin_name,
        credentials,
        !!values.sandbox_mode,
        editingRegistrar?.id
      );

      if (response.success) {
        message.success('连接测试成功');
      } else {
        message.error(response.error || '连接测试失败');
      }
    } catch (error) {
      message.error('连接测试失败');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSyncPricing = async (registrarId) => {
    setSyncingPricing(true);
    setSyncingRowId(registrarId);
    try {
      const response = await registrarService.syncPricing(registrarId);

      if (response.success) {
        const synced = response.synced_count ?? response.data?.synced_count ?? 0;
        const total = response.total_tlds ?? response.data?.total_tlds ?? 0;
        message.success(`同步成功: ${synced}/${total} 个TLD已更新`);
        setSyncResultModal({
          visible: true,
          result: {
            registrarId,
            synced,
            total,
            raw: response.data || response,
          },
        });
        fetchRegistrars();
        fetchHealth();
      } else {
        message.error(response.error || '同步失败');
      }
    } catch (error) {
      message.error('同步失败: ' + (error.message || error.error || ''));
    } finally {
      setSyncingPricing(false);
      setSyncingRowId(null);
    }
  };

  // Per-row "Test connection" — re-uses saved credentials
  const handleTestExistingConnection = async (registrar) => {
    setTestingRowId(registrar.id);
    try {
      const resp = await registrarService.testConnectionForRegistrar(registrar.id);
      if (resp.success) {
        message.success(`「${registrar.name}」 连接成功`);
      } else {
        message.error(`「${registrar.name}」 连接失败: ${resp.error || '未知错误'}`);
      }
    } catch (e) {
      message.error('测试失败: ' + (e.message || e.error || ''));
    } finally {
      setTestingRowId(null);
    }
  };

  const columns = [
    {
      title: '注册商名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => handleViewDetails(record)}>{text}</a>
      ),
    },
    {
      title: '插件代码',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: 'API类型',
      dataIndex: 'api_type',
      key: 'api_type',
    },
    {
      title: '货币',
      dataIndex: 'currency',
      key: 'currency',
      render: (currency) => <Tag>{currency || 'USD'}</Tag>,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active) =>
        is_active ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            启用
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            禁用
          </Tag>
        ),
    },
    {
      title: '能力',
      key: 'features',
      width: 320,
      render: (_, record) => {
        const caps = capabilitiesByPlugin[record.code] || [];
        const feats = record?.features || {};
        return (
          <Space wrap size={[4, 4]}>
            <Tag color={feats.dnssec === false ? 'default' : 'blue'}>DNSSEC</Tag>
            <Tag color={feats.idn === false ? 'default' : 'blue'}>IDN</Tag>
            <Tag color="blue">Glue</Tag>
            {caps.map((c) => (
              <Tag color="geekblue" key={c}>
                {CAPABILITY_LABELS[c] || c}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '模式',
      dataIndex: 'sandbox_mode',
      key: 'sandbox_mode',
      render: (sandbox_mode) =>
        sandbox_mode ? (
          <Tag color="purple">测试</Tag>
        ) : (
          <Tag color="green">正式</Tag>
        ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: '操作',
      key: 'actions',
      width: 320,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            data-testid="registrar-edit-btn"
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<ThunderboltOutlined />}
            loading={testingRowId === record.id}
            onClick={() => handleTestExistingConnection(record)}
            data-testid="registrar-test-btn"
          >
            测试连接
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SyncOutlined />}
            loading={syncingRowId === record.id}
            onClick={() => handleSyncPricing(record.id)}
            data-testid="registrar-sync-btn"
          >
            同步价格
          </Button>
          <Popconfirm
            title="确定要删除这个注册商吗？"
            description="如果有 TLD 价格关联，将自动级联删除"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const selectedPlugin = form.getFieldValue('plugin_name')
    ? plugins.find((p) => p.name === form.getFieldValue('plugin_name'))
    : null;

  const renderCredentialInput = (field) => {
    if (field.type === 'password') {
      return <Input.Password placeholder={field.description} />;
    }
    if (field.type === 'boolean') {
      return <Switch checkedChildren="是" unCheckedChildren="否" />;
    }
    return <Input placeholder={field.description} />;
  };

  return (
    <div>
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        icon={<CrownOutlined />}
        banner
        message="管理员视图 — 注册商配置直接影响全平台域名注册 / 续费 / 转移流程"
      />

      <Card
        size="small"
        title={
          <Space>
            <HistoryOutlined />
            <Text strong>同步队列健康度</Text>
          </Space>
        }
        extra={
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={healthLoading}
            onClick={fetchHealth}
            data-testid="health-refresh-btn"
          >
            刷新
          </Button>
        }
        style={{ marginBottom: 12 }}
        data-testid="sync-health-card"
      >
        <Row gutter={12}>
          <Col xs={12} sm={6}>
            <Statistic
              title="当前队列长度"
              value={syncHealth?.queue_length ?? '-'}
              valueStyle={{ color: (syncHealth?.queue_length || 0) > 100 ? '#faad14' : undefined }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic title="今日成功" value={syncStats?.summary?.success ?? syncHealth?.today_stats?.success ?? 0} valueStyle={{ color: '#52c41a' }} />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic title="今日失败" value={syncStats?.summary?.failed ?? syncHealth?.today_stats?.failed ?? 0} valueStyle={{ color: '#f5222d' }} />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic title="注册商总数" value={registrars.length} />
          </Col>
        </Row>
        {syncHealth?.recent_logs && syncHealth.recent_logs.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>最近同步:</Text>
            <div style={{ maxHeight: 100, overflow: 'auto', fontFamily: 'monospace', fontSize: 11, marginTop: 4 }}>
              {syncHealth.recent_logs.slice(0, 5).map((log, i) => (
                <div key={i} style={{ color: log.sync_status === 'success' ? '#52c41a' : '#f5222d' }}>
                  {log.synced_at} — {log.sync_status} — domain_id={log.domain_id}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>域名注册商管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} data-testid="add-registrar-btn">
          添加注册商
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={registrars}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingRegistrar ? '编辑注册商' : '添加注册商'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="插件"
            name="plugin_name"
            rules={[{ required: true, message: '请选择插件' }]}
          >
            <Select
              placeholder="选择注册商插件"
              disabled={!!editingRegistrar}
              onChange={() => form.resetFields(plugins.flatMap(p =>
                p.credential_fields?.map(f => `credential_${f.name}`) || []
              ))}
            >
              {plugins.map((plugin) => (
                <Select.Option key={plugin.name} value={plugin.name}>
                  {plugin.display_name} (v{plugin.version})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedPlugin && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary">{selectedPlugin.description}</Text>
            </Card>
          )}

          <Form.Item
            label="显示名称"
            name="display_name"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="例如：NameSilo 主账号" />
          </Form.Item>

          {selectedPlugin &&
            selectedPlugin.credential_fields &&
            selectedPlugin.credential_fields.map((field) => (
              <Form.Item
                key={field.name}
                label={field.label}
                name={`credential_${field.name}`}
                valuePropName={field.type === 'boolean' ? 'checked' : 'value'}
                rules={[
                  {
                    required: field.required,
                    message: `请输入${field.label}`,
                  },
                ]}
              >
                {renderCredentialInput(field)}
              </Form.Item>
            ))}

          <Form.Item label="描述" name="description">
            <TextArea rows={2} placeholder="备注说明" />
          </Form.Item>

          <Form.Item
            label="货币单位"
            name="currency"
            initialValue="USD"
          >
            <Select placeholder="选择货币单位">
              <Select.Option value="USD">USD (美元)</Select.Option>
              <Select.Option value="CNY">CNY (人民币)</Select.Option>
              <Select.Option value="EUR">EUR (欧元)</Select.Option>
              <Select.Option value="GBP">GBP (英镑)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="优先级" name="priority">
            <InputNumber
              min={1}
              max={1000}
              style={{ width: '100%' }}
              placeholder="数字越小优先级越高"
            />
          </Form.Item>

          <Form.Item label="启用" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            label={selectedPlugin?.name === 'domainnameapi' ? 'OT&E' : '测试模式'}
            name="sandbox_mode"
            valuePropName="checked"
            tooltip={selectedPlugin?.name === 'domainnameapi' ? 'ote.domainresellerapi.com' : '开启后将使用注册商的测试/沙盒环境'}
          >
            <Switch checkedChildren="测试" unCheckedChildren="正式" />
          </Form.Item>

          <Form.Item label="支持DNSSEC" name="dnssec" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item label="支持IDN" name="idn" valuePropName="checked">
            <Switch />
          </Form.Item>

          {!editingRegistrar && (
            <>
              <Form.Item
                label="测试连接"
                name="test_connection"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="自动同步TLD价格"
                name="auto_sync_pricing"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          )}

          {selectedPlugin && (
            <Form.Item>
              <Button
                type="dashed"
                icon={<ApiOutlined />}
                onClick={handleTestConnection}
                loading={testingConnection}
                block
              >
                测试连接
              </Button>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Drawer
        title="注册商详情"
        placement="right"
        width={600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedRegistrar && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="名称">
              {selectedRegistrar.name}
            </Descriptions.Item>
            <Descriptions.Item label="插件代码">
              <Tag color="blue">{selectedRegistrar.code}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="API类型">
              {selectedRegistrar.api_type}
            </Descriptions.Item>
            <Descriptions.Item label="优先级">
              {selectedRegistrar.priority}
            </Descriptions.Item>
            <Descriptions.Item label="货币单位">
              {selectedRegistrar.currency || 'USD'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {selectedRegistrar.is_active ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  启用
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  禁用
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="运行模式">
              {selectedRegistrar.sandbox_mode ? (
                <Tag color="purple">测试环境</Tag>
              ) : (
                <Tag color="green">正式环境</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="API凭证">
              {selectedRegistrar.has_credentials ? (
                <div>
                  <Tag color="green">已配置</Tag>
                  {selectedRegistrar.api_credentials_masked && (
                    <div style={{ marginTop: 8 }}>
                      {Object.entries(selectedRegistrar.api_credentials_masked).map(([key, value]) => (
                        <div key={key} style={{ marginBottom: 4 }}>
                          <Text strong>{key}:</Text> <Text code>{value}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Tag color="red">未配置</Tag>
              )}
            </Descriptions.Item>
        <Descriptions.Item label="支持的TLD数量">
          {selectedRegistrar.tld_count}
        </Descriptions.Item>
        <Descriptions.Item label="能力">
          <Space wrap>
            <Tag color={selectedRegistrar?.features?.dnssec === false ? 'default' : 'blue'}>DNSSEC</Tag>
            <Tag color={selectedRegistrar?.features?.idn === false ? 'default' : 'blue'}>IDN</Tag>
            <Tag color="blue">Glue</Tag>
            {(capabilitiesByPlugin[selectedRegistrar.code] || []).map((c) => (
              <Tag color="geekblue" key={c}>{CAPABILITY_LABELS[c] || c}</Tag>
            ))}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="备注">
          {selectedRegistrar.notes || '-'}
        </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {selectedRegistrar.created_at}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {selectedRegistrar.updated_at}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      <Modal
        title="同步结果"
        open={syncResultModal.visible}
        onCancel={() => setSyncResultModal({ visible: false, result: null })}
        footer={[
          <Button key="close" onClick={() => setSyncResultModal({ visible: false, result: null })}>关闭</Button>,
        ]}
        width={600}
      >
        {syncResultModal.result && (
          <>
            <Statistic
              title={`已更新 ${syncResultModal.result.synced} / ${syncResultModal.result.total} 个 TLD`}
              value={syncResultModal.result.total > 0
                ? `${Math.round(syncResultModal.result.synced / syncResultModal.result.total * 100)}%`
                : '0%'}
            />
            <Paragraph type="secondary" style={{ marginTop: 12 }}>
              注：本次同步逐 TLD 处理，单个失败不影响其他；部分失败会留下成功的更新。
              查看 <Text code>domain_sync_logs</Text> 表了解详情，或刷新「同步队列健康度」卡片。
            </Paragraph>
            <details>
              <summary style={{ cursor: 'pointer' }}>原始响应数据</summary>
              <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 11 }}>
                {JSON.stringify(syncResultModal.result.raw, null, 2)}
              </pre>
            </details>
          </>
        )}
      </Modal>
    </div>
  );
}
